/**
 * LA TIENDA DEL PADEL — Predictive Search
 * Opens a full-screen overlay with debounced product/article search.
 */
class PredictiveSearch {
  constructor() {
    this.overlay = document.getElementById('predictive-search-overlay');
    if (!this.overlay) return;

    this.input = this.overlay.querySelector('[data-predictive-search-input]');
    this.form = this.overlay.querySelector('[data-predictive-search-form]');
    this.resultsContainer = this.overlay.querySelector('[data-predictive-search-results]');
    this.productsGrid = this.overlay.querySelector('[data-predictive-search-products]');
    this.articlesList = this.overlay.querySelector('[data-predictive-search-articles]');
    this.productsSection = this.overlay.querySelector('[data-predictive-search-products-section]');
    this.articlesSection = this.overlay.querySelector('[data-predictive-search-articles-section]');
    this.popularSection = this.overlay.querySelector('[data-predictive-search-popular]');
    this.loadingEl = this.overlay.querySelector('[data-predictive-search-loading]');
    this.noResultsEl = this.overlay.querySelector('[data-predictive-search-no-results]');
    this.closeBtn = this.overlay.querySelector('[data-predictive-search-close]');
    this.viewAllLink = this.overlay.querySelector('[data-predictive-search-view-all]');

    this.debounceTimer = null;
    this.debounceDelay = 300;
    this.abortController = null;
    this.isOpen = false;

    this.bindEvents();
  }

  bindEvents() {
    // Open search from header search buttons
    document.querySelectorAll('[data-search-open], .site-header__action-btn--search, .mobile-menu__bottom-link[href="/search"]').forEach((btn) => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        this.open();
      });
    });

    // Close
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }

    // Close on overlay background click
    this.overlay.addEventListener('click', (e) => {
      if (e.target === this.overlay) {
        this.close();
      }
    });

    // Close on Escape
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.isOpen) {
        this.close();
      }
    });

    // Input handling with debounce
    if (this.input) {
      this.input.addEventListener('keyup', () => {
        this.debounce(() => this.search());
      });
    }

    // Form submit → navigate to search page
    if (this.form) {
      this.form.addEventListener('submit', (e) => {
        const query = this.input.value.trim();
        if (!query) {
          e.preventDefault();
          return;
        }
        // Allow default form submission to /search?q=...
      });
    }
  }

  open() {
    this.isOpen = true;
    this.overlay.classList.add('is-active');
    document.body.classList.add('no-scroll');

    // Force reflow for transition
    this.overlay.offsetHeight;
    this.overlay.classList.add('is-visible');

    // Focus input after transition
    setTimeout(() => {
      if (this.input) this.input.focus();
    }, 100);

    // Trap focus within overlay
    this.trapFocus();
  }

  close() {
    this.isOpen = false;
    this.overlay.classList.remove('is-visible');
    document.body.classList.remove('no-scroll');

    // Remove after transition
    setTimeout(() => {
      this.overlay.classList.remove('is-active');
    }, 200);

    // Release focus trap
    this.releaseFocus();
  }

  trapFocus() {
    this._focusableElements = this.overlay.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    this._handleTab = (e) => {
      if (e.key !== 'Tab') return;
      const focusable = Array.from(this._focusableElements);
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', this._handleTab);
  }

  releaseFocus() {
    if (this._handleTab) {
      document.removeEventListener('keydown', this._handleTab);
    }
  }

  debounce(callback) {
    clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(callback, this.debounceDelay);
  }

  async search() {
    const query = this.input.value.trim();

    // Empty query — show popular
    if (!query) {
      this.showPopular();
      return;
    }

    // Abort previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    this.abortController = new AbortController();

    this.showLoading();

    try {
      const response = await fetch(
        `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,article,page&resources[limit]=10`,
        { signal: this.abortController.signal }
      );

      if (!response.ok) throw new Error('Search request failed');

      const data = await response.json();
      this.renderResults(data, query);
    } catch (error) {
      if (error.name === 'AbortError') return;
      console.error('Predictive search error:', error);
      this.showNoResults();
    }
  }

  renderResults(data, query) {
    const resources = data.resources;
    const products = resources?.results?.products || [];
    const articles = resources?.results?.articles || [];
    const pages = resources?.results?.pages || [];

    this.hideLoading();

    if (products.length === 0 && articles.length === 0 && pages.length === 0) {
      this.showNoResults();
      return;
    }

    // Hide popular, no-results
    this.hidePopular();
    this.hideNoResults();

    // Render products
    if (products.length > 0 && this.productsGrid && this.productsSection) {
      this.productsSection.style.display = '';
      this.productsGrid.innerHTML = products.map((product) => this.renderProductCard(product)).join('');
    } else if (this.productsSection) {
      this.productsSection.style.display = 'none';
    }

    // Render articles + pages together
    const contentItems = [
      ...articles.map((a) => this.renderArticleItem(a)),
      ...pages.map((p) => this.renderPageItem(p))
    ];
    if (contentItems.length > 0 && this.articlesList && this.articlesSection) {
      this.articlesSection.style.display = '';
      this.articlesList.innerHTML = contentItems.join('');
    } else if (this.articlesSection) {
      this.articlesSection.style.display = 'none';
    }

    // Update view-all link
    if (this.viewAllLink) {
      this.viewAllLink.querySelector('a').href = `/search?q=${encodeURIComponent(query)}`;
    }

    // Show results container
    this.resultsContainer.classList.add('has-results');
  }

  renderProductCard(product) {
    const image = product.image
      ? `<img
          src="${this.getSizedImageUrl(product.image, '400x')}"
          srcset="${this.getSizedImageUrl(product.image, '200x')} 200w, ${this.getSizedImageUrl(product.image, '400x')} 400w"
          sizes="(max-width: 749px) 45vw, 240px"
          alt="${this.escapeHtml(product.title)}"
          loading="lazy"
          class="product-card__image product-card__image--primary"
        >`
      : `<div class="product-card__image-placeholder">
          <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1" aria-hidden="true">
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"/>
            <circle cx="8.5" cy="8.5" r="1.5"/>
            <polyline points="21 15 16 10 5 21"/>
          </svg>
        </div>`;

    const price = product.price
      ? `<span class="price__regular">${this.formatMoney(product.price)}</span>`
      : '';

    return `
      <div class="product-card product-card--search">
        <div class="product-card__image-wrapper">
          <a href="${product.url}" class="product-card__image-link" aria-label="${this.escapeHtml(product.title)}">
            ${image}
          </a>
        </div>
        <div class="product-card__content">
          <h3 class="product-card__title">
            <a href="${product.url}">${this.escapeHtml(product.title)}</a>
          </h3>
          <div class="product-card__price">
            <div class="price">${price}</div>
          </div>
        </div>
      </div>
    `;
  }

  renderArticleItem(article) {
    return `
      <li class="predictive-search__article-item">
        <a href="${article.url}" class="predictive-search__article-link">
          ${this.escapeHtml(article.title)}
        </a>
      </li>
    `;
  }

  renderPageItem(page) {
    return `
      <li class="predictive-search__article-item">
        <a href="${page.url}" class="predictive-search__article-link">
          ${this.escapeHtml(page.title)}
        </a>
      </li>
    `;
  }

  showLoading() {
    if (this.loadingEl) this.loadingEl.classList.add('is-active');
    if (this.resultsContainer) this.resultsContainer.classList.remove('has-results');
    this.hidePopular();
    this.hideNoResults();
  }

  hideLoading() {
    if (this.loadingEl) this.loadingEl.classList.remove('is-active');
  }

  showPopular() {
    this.hideLoading();
    this.hideNoResults();
    if (this.resultsContainer) this.resultsContainer.classList.remove('has-results');
    if (this.popularSection) this.popularSection.style.display = '';
  }

  hidePopular() {
    if (this.popularSection) this.popularSection.style.display = 'none';
  }

  showNoResults() {
    this.hideLoading();
    this.hidePopular();
    if (this.resultsContainer) this.resultsContainer.classList.remove('has-results');
    if (this.noResultsEl) this.noResultsEl.classList.add('is-active');
  }

  hideNoResults() {
    if (this.noResultsEl) this.noResultsEl.classList.remove('is-active');
  }

  /**
   * Convert a Shopify image URL to a sized variant.
   * Handles both old CDN format (image.jpg → image_400x.jpg)
   * and new CDN format (using ?width= query parameter).
   */
  getSizedImageUrl(src, size) {
    if (!src) return '';
    // Extract numeric width from size string like '400x'
    const widthMatch = size.match(/^(\d+)/);
    const width = widthMatch ? widthMatch[1] : '400';
    // Use Shopify CDN width parameter (works with all URL formats)
    try {
      const url = new URL(src, window.location.origin);
      url.searchParams.set('width', width);
      return url.toString();
    } catch (e) {
      // Fallback: append width parameter
      const separator = src.includes('?') ? '&' : '?';
      return `${src}${separator}width=${width}`;
    }
  }

  /**
   * Format a price to the store's currency format.
   * Shopify suggest API returns price as a string like "1000.00"
   * (already in the store's currency, NOT in cents).
   */
  formatMoney(price) {
    if (typeof price === 'string') {
      // Remove any existing currency symbols, commas, or spaces
      price = parseFloat(price.replace(/[^0-9.]/g, ''));
    }
    if (isNaN(price)) return '';
    return `EGP ${Math.round(price).toLocaleString('en-US')}`;
  }

  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => new PredictiveSearch());
} else {
  new PredictiveSearch();
}
